/*
 * AMAVYA · musique d'ambiance « Les rêves d'AMAVYA ».
 * Coupée par défaut : le visiteur l'active d'un appui, son choix est gardé et la musique
 * reprend de page en page là où elle s'était arrêtée. Boucle sans coupure grâce à Web Audio.
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var AC = window.AudioContext || window.webkitAudioContext;
  if (!script || !script.dataset.src || !AC || !window.fetch || !window.Promise) return;
  if (document.querySelector('.amv-musique')) return;

  var CLE_CHOIX = 'amavya-musique';
  var CLE_POSITION = 'amavya-musique-position';
  var VOLUME = 0.5;
  var FONDU_ENTREE = 1.6;
  var FONDU_SORTIE = 0.35;
  var SEUIL_ATTAQUE = 0.001;
  var GESTES = ['click', 'touchend', 'keydown'];

  var reglages = {
    src: script.dataset.src,
    duree: parseFloat(script.dataset.duree) || 0,
    libelle: script.dataset.libelle || 'Musique',
    activer: script.dataset.activer || 'Activer la musique',
    couper: script.dataset.couper || 'Couper la musique',
    reprendre: script.dataset.reprendre || 'Reprendre la musique'
  };

  var bouton = null;
  var ctx = null;
  var volume = null;
  var tampon = null;
  var chargement = null;
  var source = null;
  var debutBoucle = 0;
  var finBoucle = 0;
  var departHorloge = 0;
  var departPosition = 0;
  var positionReprise = 0;
  var ecouteGestes = false;
  var etat = 'coupe';

  function lire(stockage, cle) {
    try {
      return window[stockage].getItem(cle);
    } catch (erreur) {
      return null;
    }
  }

  function ecrire(stockage, cle, valeur) {
    try {
      window[stockage].setItem(cle, valeur);
    } catch (erreur) {
      console.warn('AMAVYA musique : choix non mémorisé', erreur);
    }
  }

  function changerEtat(nouvelEtat) {
    etat = nouvelEtat;
    if (!bouton) return;
    var active = nouvelEtat === 'joue' || nouvelEtat === 'chargement';
    var texte = active ? reglages.couper : nouvelEtat === 'attente' ? reglages.reprendre : reglages.activer;
    bouton.setAttribute('data-etat', nouvelEtat);
    bouton.setAttribute('aria-pressed', active ? 'true' : 'false');
    bouton.setAttribute('aria-label', texte);
    bouton.title = texte;
  }

  function preparerContexte() {
    if (ctx) return;
    ctx = new AC();
    volume = ctx.createGain();
    volume.gain.value = 0;
    volume.connect(ctx.destination);
  }

  // Sur iPhone, sans ce réglage, le mode silencieux coupe le son de Web Audio.
  function sessionLecture() {
    if (!navigator.audioSession) return;
    try {
      navigator.audioSession.type = 'playback';
    } catch (erreur) {
      console.warn('AMAVYA musique : session audio non réglée', erreur);
    }
  }

  // Chrome laisse resume() en suspens sans geste du visiteur : le délai tranche.
  function reprendreContexte() {
    if (ctx.state === 'running') return Promise.resolve(true);
    var reprise = ctx.resume();
    var delai = new Promise(function (fin) {
      setTimeout(fin, 600);
    });
    return Promise.race([reprise, delai]).then(function () {
      return ctx.state === 'running';
    }, function () {
      return false;
    });
  }

  function decoder(donnees) {
    return new Promise(function (resoudre, rejeter) {
      var promesse = ctx.decodeAudioData(donnees, resoudre, rejeter);
      if (promesse && promesse.then) promesse.then(resoudre, rejeter);
    });
  }

  // Le fichier commence directement par la musique : le silence éventuel en tête vient du décodeur.
  function reperer(audio) {
    var gauche = audio.getChannelData(0);
    var droite = audio.numberOfChannels > 1 ? audio.getChannelData(1) : gauche;
    var i = 0;
    while (i < gauche.length && Math.abs(gauche[i]) < SEUIL_ATTAQUE && Math.abs(droite[i]) < SEUIL_ATTAQUE) i++;
    debutBoucle = i < gauche.length ? i / audio.sampleRate : 0;
    finBoucle = reglages.duree ? Math.min(audio.duration, debutBoucle + reglages.duree) : audio.duration;
  }

  function charger() {
    if (tampon) return Promise.resolve(tampon);
    if (!chargement) {
      chargement = fetch(reglages.src, { credentials: 'same-origin' })
        .then(function (reponse) {
          if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
          return reponse.arrayBuffer();
        })
        .then(decoder)
        .then(function (audio) {
          tampon = audio;
          reperer(audio);
          return audio;
        });
      chargement.catch(function () {
        chargement = null;
      });
    }
    return chargement;
  }

  function positionCourante() {
    var longueur = finBoucle - debutBoucle;
    if (!source || longueur <= 0) return positionReprise;
    return (departPosition + ctx.currentTime - departHorloge) % longueur;
  }

  function lancerSource() {
    var longueur = finBoucle - debutBoucle;
    var depart = longueur > 0 ? ((positionReprise % longueur) + longueur) % longueur : 0;
    var maintenant = ctx.currentTime;
    source = ctx.createBufferSource();
    source.buffer = tampon;
    source.loop = true;
    source.loopStart = debutBoucle;
    source.loopEnd = finBoucle;
    source.connect(volume);
    source.start(maintenant, debutBoucle + depart);
    departHorloge = maintenant;
    departPosition = depart;
    volume.gain.cancelScheduledValues(maintenant);
    volume.gain.setValueAtTime(0, maintenant);
    volume.gain.linearRampToValueAtTime(VOLUME, maintenant + FONDU_ENTREE);
  }

  function jouer(discret) {
    preparerContexte();
    sessionLecture();
    if (!discret) changerEtat('chargement');
    var attendu = etat;
    return Promise.all([reprendreContexte(), charger()]).then(function (resultats) {
      if (etat !== attendu) return;
      if (!resultats[0]) {
        changerEtat('attente');
        attendreGeste();
        return;
      }
      arreterEcoute();
      if (!source) lancerSource();
      changerEtat('joue');
    }, function (erreur) {
      console.warn('AMAVYA musique : lecture impossible', erreur);
      arreterEcoute();
      changerEtat('coupe');
    });
  }

  function arreter() {
    changerEtat('coupe');
    arreterEcoute();
    if (!ctx) return;
    if (source) {
      positionReprise = positionCourante();
      var maintenant = ctx.currentTime;
      volume.gain.cancelScheduledValues(maintenant);
      volume.gain.setValueAtTime(volume.gain.value, maintenant);
      volume.gain.linearRampToValueAtTime(0, maintenant + FONDU_SORTIE);
      source.stop(maintenant + FONDU_SORTIE + 0.05);
      source = null;
    }
    setTimeout(function () {
      if (etat === 'coupe' && ctx.state === 'running') ctx.suspend();
    }, (FONDU_SORTIE + 0.3) * 1000);
  }

  // Le choix « oui » est gardé : sur une nouvelle page, le premier appui du visiteur relance la musique.
  function surGeste(evenement) {
    if (bouton && bouton.contains(evenement.target)) return;
    if (etat === 'attente') jouer(true);
    else arreterEcoute();
  }

  function attendreGeste() {
    if (ecouteGestes) return;
    ecouteGestes = true;
    GESTES.forEach(function (type) {
      document.addEventListener(type, surGeste, true);
    });
  }

  function arreterEcoute() {
    if (!ecouteGestes) return;
    ecouteGestes = false;
    GESTES.forEach(function (type) {
      document.removeEventListener(type, surGeste, true);
    });
  }

  function surClic() {
    if (etat === 'joue' || etat === 'chargement') {
      ecrire('localStorage', CLE_CHOIX, 'non');
      arreter();
    } else {
      ecrire('localStorage', CLE_CHOIX, 'oui');
      jouer(false);
    }
  }

  function construireBouton() {
    bouton = document.createElement('button');
    bouton.type = 'button';
    bouton.className = 'amv-musique';
    var ondes = document.createElement('span');
    ondes.className = 'amv-musique__ondes';
    ondes.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 4; i++) ondes.appendChild(document.createElement('i'));
    var texte = document.createElement('span');
    texte.className = 'amv-musique__texte';
    texte.textContent = reglages.libelle;
    bouton.appendChild(ondes);
    bouton.appendChild(texte);
    bouton.addEventListener('click', surClic);
    document.body.appendChild(bouton);
    changerEtat(etat);
  }

  document.addEventListener('visibilitychange', function () {
    if (!ctx || etat !== 'joue') return;
    if (document.hidden) {
      ctx.suspend();
      return;
    }
    reprendreContexte().then(function (enMarche) {
      if (enMarche || etat !== 'joue') return;
      changerEtat('attente');
      attendreGeste();
    });
  });

  window.addEventListener('pagehide', function () {
    ecrire('sessionStorage', CLE_POSITION, String(etat === 'joue' ? positionCourante() : positionReprise));
  });

  function demarrer() {
    var memo = parseFloat(lire('sessionStorage', CLE_POSITION));
    positionReprise = isFinite(memo) && memo >= 0 ? memo : 0;
    construireBouton();
    var editeur = window.Shopify && window.Shopify.designMode;
    if (lire('localStorage', CLE_CHOIX) === 'oui' && !editeur) {
      changerEtat('attente');
      jouer(true);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})();